import { ref, reactive } from 'vue';
import { cloneDeep, get, assign } from 'lodash-es';
import { setValue } from '../utils';
import type { TableModel } from './table-model';
import {
  validateValue,
  parseValidationRules,
  type ValidationErrors,
} from '../validation';

export class RowModel<T extends Record<string, any> = any> {
  public readonly isRow = true;

  public readonly internalId = `row_${Math.random().toString(36).substr(2, 9)}`;

  public data: T; // The live, reactive working copy the editor binds to.
  private originalData: T; // Backup taken at beginEdit, restored on cancel.
  private sourceData: T; // The object the parent supplied; committed values are written back to it.

  public isEditing = ref(false);
  public isExpanded = ref(false);
  public isSelected = ref(false);
  public isSaving = ref(false);
  public isDeleting = ref(false);
  public isNew = ref(false);

  public errors = ref<ValidationErrors>({});

  private table: TableModel;

  constructor(table: TableModel, data: T) {
    this.table = table;
    this.sourceData = data;
    this.originalData = cloneDeep(data);
    this.data = reactive(cloneDeep(data)) as T;
    this.isNew.value = this.table.getRowKey(this.data) == null;
  }

  get id(): string | number | null {
    return this.table.getRowKey(this.data);
  }

  get isEditable(): boolean {
    return this.table.isRowEditable(this);
  }

  get isDeletable(): boolean {
    return this.table.isRowDeletable(this);
  }

  get key(): string {
    const id = this.id;
    return id ? `row_${id}` : this.internalId;
  }

  public getValue(fieldName: string): any {
    return get(this.data, fieldName);
  }

  public setValue(fieldName: string, value: any): void {
    setValue(this.data, fieldName, value);
    this.validateField(fieldName);
  }

  public validateField(fieldName: string): boolean {
    const col = this.table.options.columns.find((c) => c.field === fieldName);

    if (!col || (!col.validator && !col.required)) {
      delete this.errors.value[fieldName];
      return true;
    }

    const value = this.getValue(fieldName);

    const rules = parseValidationRules(col.validator, col.required);

    const error = validateValue(value, rules, col.header || col.name);

    if (error) {
      this.errors.value[fieldName] = error;
      return false;
    } else {
      delete this.errors.value[fieldName];
      return true;
    }
  }

  public validate(): boolean {
    let isValid = true;

    for (const col of this.table.options.columns) {
      if (col.editable !== false && !col.readonly && !col.systemColumn) {
        if (!this.validateField(col.field)) {
          isValid = false;
        }
      }
    }
    return isValid;
  }

  public setFocus(fieldName: string) {
    this.table.emitFocus(this.id, fieldName);
  }

  public toggleExpand() {
    this.isExpanded.value = !this.isExpanded.value;
  }

  public toggleSelect() {
    this.isSelected.value = !this.isSelected.value;
    this.table.onRowSelectionChanged(this);
  }

  public beginEdit() {
    this.table.beginEdit(this);
  }

  public onEnterEdit() {
    this.originalData = cloneDeep(this.data);

    this.isEditing.value = true;
    this.errors.value = {};
  }

  public cancelEdit() {
    this.table.cancelEdit(this);
  }

  public onCancelEdit() {
    const fresh = this.originalData as Record<string, any>;
    const target = this.data as Record<string, any>;
    for (const key of Object.keys(target)) {
      if (!(key in fresh)) {
        delete target[key];
      }
    }
    assign(target, cloneDeep(fresh));
    this.isEditing.value = false;
    this.errors.value = {};
  }

  public async save() {
    if (this.isSaving.value || this.table.commitPending.value) {
      return;
    }
    if (!this.validate()) {
      return;
    }

    this.isSaving.value = true;
    this.table.beginCommit();
    try {
      const result = await this.table.saveRow(this);
      this.commitData(result);
      this.table.commitEdit(this);
      this.table.endCommit();
    } catch (e) {
      console.error('Save failed', e);
      this.table.endCommit();
    } finally {
      this.isSaving.value = false;
    }
  }

  private commitData(result?: any) {
    if (result && typeof result === 'object') {
      assign(this.data, result);
    }
    if (this.sourceData && this.sourceData !== this.data) {
      assign(this.sourceData as Record<string, any>, cloneDeep(this.data));
    }
    this.originalData = cloneDeep(this.data);
    this.isNew.value = this.table.getRowKey(this.data) == null;
    this.errors.value = {};
  }

  public async delete() {
    if (
      this.isEditing.value ||
      this.isDeleting.value ||
      this.table.commitPending.value
    ) {
      return;
    }
    this.isDeleting.value = true;
    this.table.beginCommit();
    try {
      await this.table.deleteRow(this);
    } catch (e) {
      console.error('Delete failed/cancelled', e);
    } finally {
      this.isDeleting.value = false;
      this.table.endCommit();
    }
  }
}
